import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IMetricAlert } from 'types/interfaces/iMetricAlert';
import { loadMachineMetricsAlertHistory } from 'redux/metrics_alert_history/actions';
import { getMetricsAlertHistoryData } from 'redux/metrics_alert_history/selectors';
import { Column, Order, Table } from 'web-components-ts';
import { Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import T from 'prop-types';
import { HeadingWrapper, StyledMarginDiv, StyledPaper } from './Alerts.styled';
import Alarms from '../Alarms';
import { isValidDateString } from '../../../helpers/utils';
import { defaultEmptyCellValue } from '../../../helpers/stringHandler';

interface AlarmsProps {
  machineId: string;
  from: string;
  to: string;
}

const Alerts = ({ machineId, from, to }: AlarmsProps) => {
  const { t, i18n } = useTranslation();

  const dispatch = useDispatch();

  const rowsPerPage = 5;
  const firstPage = 1;
  const [alertHistoryList, setAlertHistoryList] = useState<IMetricAlert[]>([]);
  const [currentPage, setCurrentPage] = useState(firstPage);
  const [totalPages, setTotalPages] = useState(0);
  const [sortBy, setSortBy] = useState('created_at');
  const [order, setOrder] = useState('DESC');

  const { data: getMetricsAlertHistoryResponse } = useSelector(getMetricsAlertHistoryData);

  let locale = 'en-GB';

  switch (i18n.language) {
    case 'en_US':
      locale = 'en-GB';
      break;
    case 'de_DE':
      locale = 'de';
      break;
    case 'pt_BR':
      locale = 'pt-BR';
      break;
    case 'es_ES':
      locale = 'es';
      break;
    default:
      locale = 'en-GB';
  }

  const formatDate = (date: string) =>
    new Date(date).toLocaleString(locale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });

  const getDateValue = (dateStr: string) => (isValidDateString(dateStr) ? formatDate(dateStr) : defaultEmptyCellValue);

  useEffect(() => {
    dispatch(
      loadMachineMetricsAlertHistory({
        page: currentPage,
        limit: rowsPerPage,
        sort: sortBy,
        order,
        machine_ids: machineId,
        from,
        to
      })
    );
  }, [dispatch]);

  const columns: Column<IMetricAlert>[] = [
    {
      field: 'created_at',
      label: t('batch_records.alerts.created_at'),
      sort: true,
      render: (row: IMetricAlert) => <span>{getDateValue(row.created_at)}</span>
    },
    {
      field: 'deleted_at',
      label: t('batch_records.alerts.deleted_at'),
      sort: true,
      render: (row: IMetricAlert) => <span>{getDateValue(row.deleted_at)}</span>
    },
    {
      field: 'message',
      label: t('batch_records.alerts.message'),
      sort: false
    }
  ];

  useEffect(() => {
    if (getMetricsAlertHistoryResponse?.data?.length) {
      setCurrentPage(getMetricsAlertHistoryResponse.page || 0);
      setTotalPages(getMetricsAlertHistoryResponse.total_pages || 0);
      setAlertHistoryList(getMetricsAlertHistoryResponse.data);
    } else {
      setAlertHistoryList([]);
    }
  }, [getMetricsAlertHistoryResponse]);

  const handleChangePage = (page: number) => {
    setCurrentPage(page);
    dispatch(
      loadMachineMetricsAlertHistory({
        page,
        order,
        sort: sortBy,
        limit: rowsPerPage,
        machine_ids: machineId,
        from,
        to
      })
    );
  };

  const handleTableSort = (orderParam: Order, orderByParam: keyof IMetricAlert) => {
    setSortBy(orderByParam);
    setOrder(orderParam.toUpperCase());
    dispatch(
      loadMachineMetricsAlertHistory({
        order: orderParam.toUpperCase(),
        sort: orderByParam,
        page: 1,
        limit: rowsPerPage,
        machine_ids: machineId,
        from,
        to
      })
    );
  };

  return (
    <div>
      <StyledMarginDiv marginTop="2rem">
        <StyledPaper elevation={3}>
          <HeadingWrapper expanded>
            <Typography variant="caption">{t('batch_records.alerts.title')}</Typography>
          </HeadingWrapper>
          <Table<IMetricAlert>
            rowKey="id"
            dataSource={alertHistoryList}
            columns={columns}
            hasFooter
            itensPerPageLabel={t('general.overview.items')}
            itemLabel={t('general.overview.of')}
            currentPage={currentPage}
            totalPages={totalPages}
            rowsPerPage={rowsPerPage}
            defaultSort={{ field: sortBy, order: order as Order }}
            handleChangePage={handleChangePage}
            handleTableSort={handleTableSort}
          />
        </StyledPaper>
      </StyledMarginDiv>
    </div>
  );
};

Alarms.propTypes = {
  machineId: T.string.isRequired,
  from: T.string.isRequired,
  to: T.string.isRequired
};

export default Alerts;
